import React, { useState, useRef, useEffect } from 'react';
import { Bot, User, X, MoreVertical, RefreshCw, AlertCircle } from 'lucide-react';
import useSessionManager from './SessionManager';

const SessionMenu = ({ phone }) => {
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef(null);
    const buttonRef = useRef(null);

    const sessionManager = useSessionManager({ phone });

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                menuRef.current &&
                !menuRef.current.contains(event.target) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const toggleMenu = () => {
        setIsOpen(!isOpen);
    };

    return (
        <div className="relative">
            <button
                ref={buttonRef}
                onClick={toggleMenu}
                className="p-2 hover:bg-gray-100 rounded-full flex items-center"
                aria-label="Session menu"
            >
                <div className="mr-2 flex items-center">
                    <span className="flex items-center text-xs text-gray-500">
                        {sessionManager.statusIcon}
                        <span className="ml-1">{sessionManager.statusLabel}</span>
                    </span>
                </div>
                <MoreVertical size={20} className="text-gray-500" />
            </button>

            {isOpen && (
                <div
                    ref={menuRef}
                    className="absolute right-0 top-full mt-1 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200"
                >
                    <div className="py-1">
                        <button
                            onClick={() => {
                                sessionManager.takeover();
                                setIsOpen(false);
                            }}
                            className="w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100 flex items-center"
                            disabled={sessionManager.isLoading || sessionManager.status?.mode === 'agent'}
                        >
                            <User size={16} className="mr-2" />
                            Take Over from Bot
                        </button>

                        <button
                            onClick={() => {
                                sessionManager.returnToBot();
                                setIsOpen(false);
                            }}
                            className="w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100 flex items-center"
                            disabled={sessionManager.isLoading || sessionManager.status?.mode === 'bot'}
                        >
                            <Bot size={16} className="mr-2" />
                            Return to Bot
                        </button>

                        <div className="border-t border-gray-200 my-1"></div>

                        <button
                            onClick={() => {
                                sessionManager.endSession();
                                setIsOpen(false);
                            }}
                            className="w-full px-4 py-2 text-sm text-left text-red-600 hover:bg-gray-100 flex items-center"
                            disabled={sessionManager.isLoading}
                        >
                            <X size={16} className="mr-2" />
                            End Session
                        </button>

                        <button
                            onClick={() => {
                                sessionManager.refresh();
                                setIsOpen(false);
                            }}
                            className="w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100 flex items-center"
                            disabled={sessionManager.isLoading}
                        >
                            <RefreshCw size={16} className="mr-2" />
                            Refresh Status
                        </button>
                    </div>

                    {sessionManager.error && (
                        <div className="px-4 py-2 text-xs text-red-600 bg-red-50 rounded-b-md">
                            {sessionManager.error}
                        </div>
                    )}
                </div>
            )}

            {sessionManager.isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-50 rounded-full">
                    <RefreshCw size={16} className="animate-spin text-gray-400" />
                </div>
            )}
        </div>
    );
};

export default SessionMenu; 